import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    ButtonGroup,
    Card, Col,
    Dropdown,
    Form,
    Image,
    InputGroup, Row,
    Spinner,
    ToggleButton,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { Context } from "../../index";
import {editAssignment, preAntiFraudCheckAssignment} from "../../http/bookAPI";
import { fetchReviewerProfilesOverviewApi } from "../../http/userAPI";
import { fetchReviewDataFromExtension } from "../../helpers/extensionScrape";

/**
 * ReviewSubmiterUserSide
 *
 * Объединяет:
 *  - Manual (AmazonReviewLinkSaver) — сохранение review_link
 *  - Automatic (AdminAmazonReviewParserExt) — парс через расширение + запись checked_*
 *
 * Пропсы:
 *  - assignmentData?: объект ассигмента (id, assTypeId, booksTypeId, book{...}, reviewer_name, rating, ...)
 *  - assId?: number — если assignmentData нет
 *  - defaultMode?: 'manual' | 'automatic' (по умолчанию 'manual')
 *  - onSaved?: () => void — вызывается при успешном сохранении (для рефетча родителя)
 *  - defaultLink?: string — стартовый линк для поля (опц.)
 */
const getExpectedReviewerNameForAssignment = (assignment) => {
    if (Number(assignment?.assTypeId) === 8) {
        return assignment?.reviewer_audible_name_snapshot || assignment?.reviewer_name || "";
    }

    return assignment?.reviewer_amazon_name_snapshot || assignment?.reviewer_name || "";
};

const isDesktopChromiumLikeBrowser = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;

    const ua = navigator.userAgent || "";
    const brands = Array.isArray(navigator.userAgentData?.brands)
        ? navigator.userAgentData.brands.map((brand) => brand.brand).join(" ")
        : "";
    const isMobile = Boolean(navigator.userAgentData?.mobile)
        || /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);

    if (isMobile) return false;

    const chromiumLike = /Chromium|Google Chrome|Microsoft Edge|Opera|Brave|Vivaldi/i.test(brands)
        || /\b(Chrome|Chromium|Edg|OPR|Opera|Brave|Vivaldi)\b/i.test(ua)
        || Boolean(window.chrome);
    const excluded = /Firefox|FxiOS|SamsungBrowser|DuckDuckGo/i.test(ua);
    const safariOnly = /Safari/i.test(ua) && !chromiumLike;

    return chromiumLike && !excluded && !safariOnly;
};

const ReviewSubmiterUserSide = ({
                                    assignmentData,
                                    assId,
                                    defaultMode,
                                    onSaved,
                                    defaultLink,
                                }) => {
    const { book } = useContext(Context);
    const MANUAL_ONLY_TYPES = new Set([12, 13, 16, 17, 18, 19, 20, 21, 24, 25, 26, 27]);
    const effectiveAssign = assignmentData || book?.selectedCurrentUserAssign || {};
    const assignmentId = effectiveAssign?.id || assId;
    const expectedReviewerName = getExpectedReviewerNameForAssignment(effectiveAssign);
    const supportsAutomaticReviewMode = useMemo(() => isDesktopChromiumLikeBrowser(), []);
    const prefersManual = MANUAL_ONLY_TYPES.has(Number(effectiveAssign?.assTypeId));
    const canUseAutomaticMode = supportsAutomaticReviewMode && !prefersManual;
    const getInitialMode = () => (
        canUseAutomaticMode && defaultMode !== "manual" ? "automatic" : "manual"
    );

    const [preCheckLoading, setPreCheckLoading] = useState(false);
    const [preCheck, setPreCheck] = useState(null);
    const [mode, setMode] = useState(getInitialMode);
    useEffect(() => {
        const currentAssign = assignmentData || book?.selectedCurrentUserAssign || {};
        const forceManual = MANUAL_ONLY_TYPES.has(Number(currentAssign?.assTypeId))
            || !supportsAutomaticReviewMode
            || defaultMode === "manual";
        setMode(forceManual ? "manual" : "automatic");
    }, [assignmentData?.assTypeId, book?.selectedCurrentUserAssign?.assTypeId, defaultMode, supportsAutomaticReviewMode]);

    useEffect(() => {
        // При смене ассигмента — мягкий ресет пользовательских полей
        setManualUrl(defaultLink || effectiveAssign?.review_link || "");
        setAutoUrl(defaultLink || effectiveAssign?.review_link || "");
        setReviewData(null);
        setError("");
        setServerMsg(null);
        setPreCheck(null);
        setPreCheckLoading(false);

        // Фиксируем режим согласно типу ассигмента:
        const forceManual = MANUAL_ONLY_TYPES.has(Number(effectiveAssign?.assTypeId))
            || !supportsAutomaticReviewMode
            || defaultMode === "manual";
        setMode(forceManual ? "manual" : "automatic");
    }, [assignmentId, defaultMode, supportsAutomaticReviewMode]);

    const ok = (msg) => toast.success(msg, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
    const fail = (msg) => toast.error(msg, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

    // Запрещаем "ссылки на создание/спасибо за отзыв" вместо публичной страницы отзыва
    const isForbiddenThankYouUrl = (u = "") => {
        try {
            const url = new URL(u.trim());
            const hostOk = /\.amazon\./i.test(url.hostname); // любой amazon TLD
            if (!hostOk) return false;

            const path = url.pathname.toLowerCase();

            // Явные кейсы:
            // /review/create-review..., /review/review-your-purchases..., часто с /thankyou
            if (/^\/review\/(create-review|review-your-purchases)(\/|$)/i.test(path)) return true;

            // Подстрахуемся от любых /review/.../thankyou
            if (path.startsWith("/review/") && path.includes("thankyou")) return true;

            // Иногда попадаются ?channel=...-thankyou
            const sp = url.search.toLowerCase();
            if (sp.includes("channel=") && sp.includes("thankyou")) return true;

            return false;
        } catch {
            return false;
        }
    };


    // ───────────────────────────────────────────────────────────────────────────────
    // MANUAL (AmazonReviewLinkSaver)
    // ───────────────────────────────────────────────────────────────────────────────
    const staffMsgManual = effectiveAssign?.review_link_staff_comment?.trim();
    const [manualUrl, setManualUrl] = useState(
        defaultLink || effectiveAssign?.review_link || ""
    );
    const [manualLoading, setManualLoading] = useState(false);
    const [manualError, setManualError] = useState("");
    const [manualAlert, setManualAlert] = useState({
        variant: "info",
        text:
            "To speed up the approval of your review, paste the link below and save it. This is the best way to help our team find your review quickly.",
    });
    const [automaticAlert, setAutomaticAlert] = useState({
        variant: "warning",
        text:
            "This is an automatic review validator that runs through your browser. Before using, please read the short guide. Key points: use Chrome (only), make sure you are logged into any Amazon account, and have the Virtalibry Review Capture extension installed in Chrome browser.",
    });

    useEffect(() => {
        if (staffMsgManual) {
            setManualAlert({
                variant: "warning",
                text: `<span class="fw-semibold">Message from Staff:</span> ${staffMsgManual}`,
            });
        }
    }, [staffMsgManual]);

    const runPreAntiFraudCheck = async (dataFromExt) => {
        if (!assignmentId) return;

        setPreCheckLoading(true);
        setPreCheck(null);
        try {
            const payload = {
                assignmentId,
                review_link: dataFromExt?.review_link || autoUrl || null,
                // сервер сам умеет извлечь review_id из ссылки;
                // если расширение уже вернуло — можно передать:
                amz_review_id: dataFromExt?.amz_review_id || null,
                checked_review_name: dataFromExt?.review_name || null,
                checked_review_text: dataFromExt?.review_text || null,
                checked_review_date: dataFromExt?.review_date || null,
            };
            const resp = await preAntiFraudCheckAssignment(payload);
            setPreCheck(resp);
        } catch (e) {
            // мягко деградируем: просто покажем подсказку
            setPreCheck({ ok: true });
        } finally {
            setPreCheckLoading(false);
        }
    };


    const handleManualSave = async () => {
        setManualError("");
        if (!assignmentId) {
            setManualError("Assignment ID is missing. Please refresh or contact support.");
            return;
        }
        const url = (manualUrl || "").trim();
        if (!url) { setManualError("Please paste your Amazon review link."); return; }
        const isAmazonLike = /^https?:\/\/[^/]*amazon\.[^/]+\/.+/i.test(url);
        if (!isAmazonLike) { setManualError("Please enter a valid Amazon link."); return; }
        if (isForbiddenThankYouUrl(url)) {
            setManualError(
                "The entered link was NOT saved because it is not valid. " +
                "This URL looks like a “leave a review / thank you” page, not a public review link. " +
                "Please open your published review (it should contain '/gp/customer-reviews/' or '/review/R…') and paste that link."
            );
            return;
        }

        setManualLoading(true);
        try {
            await editAssignment({ id: assignmentId, review_link: url });
            ok("Saved! We’ll check your review manually.");
            setManualAlert({ variant: "secondary", text: "Link saved. We’ll verify it manually within 24–48 hours." });
            onSaved?.(); // ⬅ перелистываем родителя
        } catch {
            setManualError("Couldn’t save the link. Please try again in a moment.");
            fail("Couldn’t save the link. Please try again in a moment.");
        } finally {
            setManualLoading(false);
        }
    };

    // ───────────────────────────────────────────────────────────────────────────────
    // AUTOMATIC (AdminAmazonReviewParserExt)
    // ───────────────────────────────────────────────────────────────────────────────
    const [autoUrl, setAutoUrl] = useState(
        defaultLink || effectiveAssign?.review_link || ""
    );
    const [loading, setLoading] = useState(false);
    const [serverMsg, setServerMsg] = useState(null); // {variant,text}
    const [error, setError] = useState("");
    const [reviewData, setReviewData] = useState(null);
    const [reviewerProfiles, setReviewerProfiles] = useState([]);

    const isVideoUrl = (u = "") =>
        /\.mp4(\?|$)/i.test(u) || u.includes("/vse-vms-transcoding-artifact-");

    const renderMediaThumb = (url, i) => {
        if (!url) return null;
        if (isVideoUrl(url)) {
            return (
                <video
                    key={`media-${i}`}
                    src={url}
                    preload="metadata"
                    controls
                    className="shadow rounded"
                    style={{ width: 180, borderRadius: 6 }}
                    onError={(e) => {
                        e.currentTarget.outerHTML = `
              <img 
                src="${process.env.REACT_APP_API_URL}covers/empty-cover.jpg" 
                style="width:160px; height:120px; object-fit:cover; border-radius:6px" 
                class="shadow"
                alt="Fallback"
              />
            `;
                    }}
                />
            );
        }
        return (
            <Image
                key={`media-${i}`}
                src={url}
                alt={`Review Media ${i + 1}`}
                className="shadow"
                style={{ width: 180, objectFit: "cover", borderRadius: 6 }}
                onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
                }}
                onClick={() => window.open(url, "_blank")}
            />
        );
    };


    useEffect(() => {
        if (typeof defaultLink === "string" && defaultLink.trim()) setAutoUrl(defaultLink);
        else if (typeof effectiveAssign?.review_link === "string") setAutoUrl(effectiveAssign.review_link);
    }, [defaultLink, effectiveAssign?.review_link]);

    useEffect(() => {
        if (!assignmentId) return;

        let cancelled = false;
        fetchReviewerProfilesOverviewApi()
            .then((data) => {
                if (!cancelled) {
                    setReviewerProfiles(Array.isArray(data?.profiles) ? data.profiles : []);
                }
            })
            .catch((error) => {
                console.error('Failed to load reviewer profiles for review validation:', error);
                if (!cancelled) setReviewerProfiles([]);
            });

        return () => {
            cancelled = true;
        };
    }, [assignmentId]);

    const BOOK_TYPE_LABEL = { 1: "Kindle", 2: "Paperback", 3: "Hardcover", 4: "Audio" };

    const getExpectedAsinByType = (booksTypeId, bookObj) => {
        if (!bookObj) return null;
        switch (Number(booksTypeId)) {
            case 1: return bookObj.kindle_asin || null;
            case 2: return bookObj.paper_back_asin || null;
            case 3: return bookObj.hard_cover_asin || null;
            case 4: return bookObj.audio_asin || bookObj.audio_audible_asin || null;
            default: return null;
        }
    };

    const isAsinOfThisBook = (asin, bookObj) => {
        if (!asin || !bookObj) return false;
        const list = [
            bookObj.kindle_asin,
            bookObj.paper_back_asin,
            bookObj.hard_cover_asin,
            bookObj.audio_asin,
            bookObj.audio_audible_asin,
        ].filter(Boolean);
        return list.includes(asin);
    };

    const normalize = (s) =>
        (s || "")
            .normalize("NFKD")
            .replace(/\p{M}/gu, "")
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, "");

    const allowedReviewerNames = useMemo(() => {
        const isAudibleAssignment = Number(effectiveAssign?.assTypeId) === 8;
        const profileNames = reviewerProfiles
            .filter((profile) => profile.status === 'ACTIVE')
            .map((profile) => isAudibleAssignment ? profile.audible_name : profile.amazon_name);

        return [
            expectedReviewerName,
            effectiveAssign?.reviewer_name,
            ...profileNames,
        ].filter((name) => String(name || '').trim());
    }, [effectiveAssign?.assTypeId, effectiveAssign?.reviewer_name, expectedReviewerName, reviewerProfiles]);

    const reviewerNameAllowed = (name) => {
        const gotName = normalize(name);
        if (!gotName) return false;

        return allowedReviewerNames.some((allowedName) => (
            normalize(allowedName) === gotName
        ));
    };

    const expectsVerified = (assTypeId) => {
        const MUST = new Set([3,4,5,6,11,12,13,15,16,17]);
        return MUST.has(Number(assTypeId));
    };

    const analysis = useMemo(() => {
        if (!reviewData || !effectiveAssign) return { mismatches: [], asinNote: null };
        const mm = [];
        let asinNote = null;

        const expASIN = getExpectedAsinByType(effectiveAssign?.booksTypeId, effectiveAssign?.book);
        const gotASIN = (reviewData?.asin || "").trim();
        const fromThisBook = isAsinOfThisBook(gotASIN, effectiveAssign?.book);

        if (gotASIN) {
            if (!fromThisBook) {
                mm.push({ field: "ASIN", expected: expASIN || "(ASIN for selected type)", got: gotASIN, note: "ASIN does not belong to this book." });
            } else if (expASIN && gotASIN !== expASIN) {
                asinNote = `ASIN is from this book but a different format: expected ${BOOK_TYPE_LABEL[effectiveAssign?.booksTypeId]} (${expASIN}), got ${gotASIN}.`;
            }
        }

        const gotName = normalize(reviewData?.review_name);
        if (gotName && !reviewerNameAllowed(reviewData?.review_name)) {
            mm.push({ field: "Reviewer name", expected: allowedReviewerNames.join(' / ') || expectedReviewerName, got: reviewData?.review_name, note: "Name does not match any reviewer profile for this user." });
        }

        const needVerified = expectsVerified(effectiveAssign?.assTypeId);
        const gotVerified = /verified/i.test(reviewData?.review_status || "");
        if (needVerified !== gotVerified) {
            mm.push({
                field: "Verification",
                expected: needVerified ? "Verified Purchase" : "Unverified allowed",
                got: reviewData?.review_status || "—",
                note: "Verification status does not match assignment requirements.",
            });
        }

        const expRating = Number(effectiveAssign?.rating);
        const gotRating = Number(reviewData?.review_rating);
        if (!Number.isNaN(expRating) && !Number.isNaN(gotRating) && expRating > 0 && gotRating > 0 && expRating !== gotRating) {
            mm.push({ field: "Rating", expected: `${expRating}★`, got: `${gotRating}★`, note: "Rating differs." });
        }

        return { mismatches: mm, asinNote };
    }, [reviewData, effectiveAssign, allowedReviewerNames, expectedReviewerName]);

    const fetchReview = async () => {
        const url = (autoUrl || "").trim();
        if (!url) { setError("Please enter a valid Amazon review URL."); return; }
        if (isForbiddenThankYouUrl(url)) {
            setManualError(
                "The entered link was NOT saved because it is not valid. " +
                "This URL looks like a “leave a review / thank you” page, not a public review link. " +
                "Please open your published review (it should contain '/gp/customer-reviews/' or '/review/R…') and paste that link."
            );
            return;
        }
        setLoading(true);
        setError("");
        setServerMsg(null);
        setReviewData(null);

        try {
            const data = await fetchReviewDataFromExtension(url, { timeoutMs: 15000 });
            setReviewData(data);
            ok("Review Data Fetched.");
            await runPreAntiFraudCheck(data);
        } catch {
            // мягкий фоллбек — как у вас: если не получилось проверить, всё равно сохраняем линк вручную
            setError("We couldn’t check it automatically. You may still save the link in Manual mode.");
        } finally {
            setLoading(false);
        }
    };

    const saveParsedToDb = async () => {
        if (!assignmentId) { setServerMsg({ variant: "danger", text: "Assignment ID is missing." }); return; }
        if (!reviewData) { setServerMsg({ variant: "warning", text: "Nothing to save. Fetch review data first." }); return; }

        setLoading(true);
        setServerMsg(null);

        try {
            const nowMin = () => Math.floor(Date.now() / 60000);
            const updated = await editAssignment({
                id: assignmentId,
                assignment_status: "SUCCESS",
                end_rev_date: nowMin(),
                reviewer_reading_step: "STEP5",
                review_link: reviewData.review_link,
                checked_asin: reviewData.asin,
                checked_review_name: reviewData.review_name,
                checked_review_status: reviewData.review_status,
                checked_review_link: reviewData.review_link,
                checked_review_photo: reviewData.review_photo,
                checked_review_photo2: reviewData.review_photo2,
                checked_review_photo3: reviewData.review_photo3,
                checked_review_photo4: reviewData.review_photo4,
                checked_review_photo5: reviewData.review_photo5,
                checked_review_title: reviewData.review_title,
                checked_review_rating: reviewData.review_rating,
                checked_review_country: reviewData.review_country,
                checked_review_date: reviewData.review_date,
                checked_proxy_country: reviewData.proxy_country,
                checked_review_text: reviewData.review_text,
            });

            const serverMessage = updated?.message || "Assignment updated successfully.";

            setServerMsg({ variant: "success", text: serverMessage });
            ok(serverMessage);
            onSaved?.();
        } catch (e) {
            const text =
                e?.response?.data?.message ||
                e?.message ||
                "Failed to update assignment.";
            setServerMsg({ variant: "danger", text });
            fail(text);
        } finally {
            setLoading(false);
        }
    };

    const MismatchBox = () => {
        if (!reviewData) return null;
        const { mismatches, asinNote } = analysis;
        if (mismatches.length === 0 && !asinNote) return null;
        return (
            <Alert variant="danger" className="mt-2 p-2">
                <h5
                    className={`mt-0 mb-2 ${saveGuards?.canSave ? 'text-success' : 'text-danger'}`}
                >
                    {saveGuards?.canSave ? "Looks Good!" : "We found mismatches. Please check!"}
                </h5>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mt-0 align-middle mb-0" style={{lineHeight: "18px", width: "100%" }}>
                        <thead className="table-danger">
                        <tr>
                            <th className="px-2 py-1">Field</th>
                            <th className="px-2 py-1">Expected</th>
                            <th className="px-2 py-1">Received</th>
                            <th className="px-2 py-1">Detected mismatches</th>
                        </tr>
                        </thead>
                        <tbody>
                        {analysis.mismatches.map((m, i) => (
                            <tr key={i} className="fst-italic">
                                <td className="px-2 py-1">{m.field}</td>
                                <td className="px-2 py-1">{m.expected ?? "—"}</td>
                                <td className="px-2 py-1">{m.got ?? "—"}</td>
                                <td className="px-2 py-1">{m.note ?? "—"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                {asinNote && <div className="mt-1">{asinNote}</div>}
            </Alert>
        );
    };

    const saveGuards = useMemo(() => {
        // Нет данных — сразу нельзя сохранять
        if (!reviewData) {
            return {
                asinOk: false,
                nameOk: false,
                canSave: false,
                reason: "Submit a review link first."
            };
        }

        // 1) Проверка, что ASIN принадлежит этой книге (любой формат этой книги)
        const asinOk = isAsinOfThisBook((reviewData.asin || "").trim(), assignmentData?.book);

        // 2) Проверка совпадения имени (нормализуем, чтобы избежать артефактов пробелов/регистров)
        const nameOk = reviewerNameAllowed(reviewData?.review_name);

        // Итоговый флаг
        const canSave = asinOk && nameOk;

        // Причина (для tooltip)
        let reason = "";
        if (!asinOk) reason = "ASIN does not belong to this book.";
        else if (!nameOk) reason = "Reviewer name does not match the assignment.";

        return { asinOk, nameOk, canSave, reason };
    }, [reviewData, assignmentData, reviewerNameAllowed]);

    // ───────────────────────────────────────────────────────────────────────────────
    // RENDER
    // ───────────────────────────────────────────────────────────────────────────────
    return (
        <>
            <Card className="mt-2 p-3 shadow-sm border border-secondary rounded w-100">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <div className="fw-semibold fs-6 text-muted">{mode === "automatic" && canUseAutomaticMode ? "Automatic Review Checker & Auto Validator for Chrome Only:" : "Manual Review Link Saver to help Staff find your review:"}</div>
                    {canUseAutomaticMode && (
                        <ButtonGroup size="sm" aria-label="review-mode">
                            <ToggleButton
                                id="mode-automatic"
                                type="radio"
                                variant={mode === "automatic" ? "success" : "outline-success"}
                                name="mode"
                                value="automatic"
                                checked={mode === "automatic"}
                                onChange={() => setMode("automatic")}
                            >
                                Automatic Mode
                            </ToggleButton>
                            <ToggleButton
                                id="mode-manual"
                                type="radio"
                                variant={mode === "manual" ? "success" : "outline-success"}
                                name="mode"
                                value="manual"
                                checked={mode === "manual"}
                                onChange={() => setMode("manual")}
                            >
                                Manual Mode
                            </ToggleButton>
                        </ButtonGroup>
                    )}

                </div>

                {/* Manual */}
                {mode === "manual" && (
                    <>
                        <Alert
                            variant={manualAlert.variant}
                            className="mb-3 mt-2 p-2"   // p-2 вместо стандартного p-3
                        >
                            <span dangerouslySetInnerHTML={{ __html: manualAlert.text }} />
                        </Alert>
                        <Form onSubmit={(e) => { e.preventDefault(); handleManualSave(); }}>
                            <InputGroup size="sm" className="mb-0 border-success">
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Paste your Amazon review link here…"
                                    value={manualUrl}
                                    onChange={(e) => setManualUrl(e.target.value)}
                                    disabled={manualLoading}
                                    className="border-success"
                                />
                                <Button
                                    size="sm"
                                    variant="success"
                                    onClick={handleManualSave}
                                    disabled={manualLoading}
                                    type="submit"
                                >
                                    {manualLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <span className="d-none d-sm-inline">Save Review Link</span>
                                            <span className="d-inline d-sm-none">Save</span>
                                        </>
                                    )}
                                </Button>
                            </InputGroup>
                        </Form>
                        {manualError && <Alert variant="danger" className="mb-0 p-2 mt-3">{manualError}</Alert>}
                    </>
                )}

                {/* Automatic */}
                {canUseAutomaticMode && mode === "automatic" && (
                    <>
                        <Alert
                            variant={automaticAlert.variant}
                            className="mb-3 mt-2 p-2"   // p-2 вместо стандартного p-3
                        >
                            <span dangerouslySetInnerHTML={{ __html: automaticAlert.text }} />
                        </Alert>

                        <div className="">
                            {serverMsg?.text && <Alert variant={serverMsg.variant || "secondary"} className="mb-2">{serverMsg.text}</Alert>}
                            {error && <Alert variant="danger" className="mb-2">{error}</Alert>}

                            <Form
                                onSubmit={(e) => { e.preventDefault(); fetchReview(); }}
                                className="mb-2"
                            >
                                <InputGroup size="sm">
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        className="border border-secondary"
                                        placeholder="Amazon review URL…"
                                        value={autoUrl}
                                        onChange={(e) => setAutoUrl(e.target.value)}
                                        disabled={loading}
                                    />
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={fetchReview}
                                        disabled={loading}
                                        type="submit"
                                    >
                                        {loading ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <>
                                                <span className="d-none d-sm-inline">Fetch Review Data</span>
                                                <span className="d-inline d-sm-none">Fetch</span>
                                            </>
                                        )}
                                    </Button>




                                </InputGroup>
                            </Form>

                            {reviewData && (
                                <Card className="mt-3 p-1 border border-secondary shadow-sm rounded bg-light">
                                    <Card.Body className="pt-2">
                                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                            <h6 className="mt-0 mb-0">Data detected from your link:</h6>

                                            <h6
                                                className={`mt-0 mb-0 ${
                                                    saveGuards?.canSave && (!preCheck || preCheck.ok !== false)
                                                        ? "text-success"
                                                        : "text-danger"
                                                }`}
                                            >
                                                {saveGuards?.canSave && (!preCheck || preCheck.ok !== false)
                                                    ? "Looks Good!"
                                                    : "Check!"}
                                            </h6>

                                            <Button
                                                variant="success"
                                                onClick={saveParsedToDb}
                                                disabled={loading || !saveGuards?.canSave || !reviewData || preCheckLoading || (preCheck && preCheck.ok === false)}
                                                size="sm"
                                                className="px-2"
                                            >
                                                {loading ? "Fetching..." : "Validate & Save"}
                                            </Button>
                                        </div>
                                        {/* Основная таблица */}
                                        {preCheckLoading && (
                                            <Alert variant="secondary"  className="mb-2 mt-3 p-2">Running anti-fraud pre-check…</Alert>
                                        )}
                                        {preCheck && preCheck.ok === false && (
                                            <Alert variant="danger"  className="mb-2 mt-3 p-2">
                                                <div className=""><span className="fw-semibold text-dark">Potential issue detected:</span> {preCheck.reason || "Please re-check your review data."}</div>
                                            </Alert>
                                        )}
                                        <MismatchBox />
                                            <div className="table-responsive mt-3 mb-0" style={{ overflowX: 'auto', paddingBottom: '8px' }}>

                                                <table className="min-w-full border border-gray-300 shadow-md rounded-lg mx-auto" style={{lineHeight: "18px", width: "100%" }}>
                                                    <thead>
                                                    <tr className="bg-secondary bg-opacity-50 text-sm">
                                                    <th className="px-2 py-1">Reviewer</th>
                                                    <th className="px-2 py-1">Expected</th>
                                                    <th className="px-2 py-1">Received</th>
                                                    <th className="px-2 py-1">Date</th>
                                                    <th className="px-2 py-1">Country</th>
                                                    <th className="px-2 py-1">ASIN</th>
                                                    <th className="px-2 py-1">Type</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {assignmentData?.checked_review_name || reviewData?.review_name || "No Name"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {assignmentData?.rating || "No Rating"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {reviewData?.review_rating ?? "N/A"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {reviewData?.review_date || "N/A"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {reviewData?.review_country || "N/A"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold text-break">
                                                        {reviewData?.asin || "No ASIN"}
                                                    </td>
                                                    <td className="px-2 py-1 fw-semibold">
                                                        {assignmentData?.assTypeId === 8
                                                            ? "Audible Review"
                                                            : (reviewData?.review_status || "Unverified Purchase")}
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Title */}
                                        <Row className="mt-1 mb-0">
                                            <Col>
                                                <strong className="text-muted mt-1">Title of Review:</strong>
                                                <p className="me-2 mb-2 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                                                    {reviewData?.review_title || "No Title"}
                                                </p>
                                                {/* Text */}
                                                <strong className="text-muted mt-2">Text of Review: </strong>
                                                <p className="text-dark text-muted" style={{ lineHeight: "1.2" }}>
                                                    {reviewData?.review_text || "No Text"}
                                                </p>
                                            </Col>
                                        </Row>

                                        {/* Media */}
                                        {(reviewData?.review_photo ||
                                            reviewData?.review_photo2 ||
                                            reviewData?.review_photo3 ||
                                            reviewData?.review_photo4 ||
                                            reviewData?.review_photo5) && (
                                            <>
                                                <strong className="text-muted me-2">Media in Review:</strong>
                                                <div className="d-flex align-items-center flex-nowrap overflow-auto" style={{ gap: 8 }}>
                                                    {[
                                                        reviewData?.review_photo,
                                                        reviewData?.review_photo2,
                                                        reviewData?.review_photo3,
                                                        reviewData?.review_photo4,
                                                        reviewData?.review_photo5,
                                                    ]
                                                        .filter(Boolean)
                                                        .map((u, i) => renderMediaThumb(u, i))}
                                                </div>
                                            </>
                                        )}


                                    </Card.Body>
                                </Card>
                            )}





                    </div>

                        {!reviewData && (
                            <Card className="mt-3 p-1 border border-secondary rounded bg-light">
                            <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                            <span className="mt-0 mb-0 fw-semibold">
                            <span className="d-none d-sm-inline">
                            Install & Use: Virtalibry Review Capture (Automatic Review Checker)
                            </span>
                            <span className="d-inline d-sm-none">
                            This tool only for desktop!
                            </span>
                            </span>

                        {/* Кнопка — теперь в Chrome Web Store */}
                        <a
                            href="https://chromewebstore.google.com/detail/virtalibry-review-capture/kbibcafojenjockmlojmclnjoeakkeag"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-success"
                            title="Open in Chrome Web Store"
                        >
                            <span className="d-none d-sm-inline">Add to Chrome</span>
                            <span className="d-inline d-sm-none">Open</span>
                        </a>
                    </Card.Header>

                    <Card.Body className="pt-3">
                {/* --- Только для моб --- */}
                <div className="d-block d-sm-none">
                    <div className="alert alert-warning p-2 mb-0">
                        Sorry, this tool only works in the desktop version of
                        <strong> Chrome</strong> (or similar Chromium browsers).
                        To use it, please open this page from a laptop or desktop computer.
                    </div>
                </div>

                {/* --- Только для десктопа --- */}
                <div className="d-none d-sm-block">

                    {/* Step list */}
                    <ol className="mb-2 ps-3">
                        <li className="mb-2">
                            Open the{" "}
                            <a
                                href="https://chromewebstore.google.com/detail/virtalibry-review-capture/kbibcafojenjockmlojmclnjoeakkeag"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Virtalibry Review Capture
                            </a>{" "}
                            page in Chrome Web Store and click <strong>Add to Chrome</strong>, then confirm{" "}
                            <strong>Add extension</strong>.
                            {/* Если подготовишь скрин — вставь: */}
                            <div className="d-flex flex-wrap gap-2 mt-1">
            <Image src="/images/store-add-to-chrome.jpg" alt="Add to Chrome" className="img-fluid rounded border" style={{maxHeight:"150px"}} />
          </div>
                        </li>

                        <li className="mb-2">
                            (Optional) Pin the extension (puzzle icon → pin) to access it faster.
                            <div className="d-flex flex-wrap gap-2 mt-1">
            <Image src="/images/chrome-pin-extension.jpg" alt="Pin the extension in Chrome" className="img-fluid rounded border" style={{maxHeight:"150px"}} />
          </div>
                        </li>

                        <li className="mb-2">
                            In the same browser, <strong>log into any active Amazon account</strong>.
                            <div className="d-flex flex-wrap gap-2 mt-1">
            <Image src="/images/amazon-login.jpg" alt="Log into Amazon" className="img-fluid rounded border" style={{maxHeight:"150px"}} />
          </div>
                        </li>

                        <li className="mb-2">
                            On Virtalibry, open your assignment → choose <strong>Automatic</strong> → click{" "}
                            <strong>Fetch Review Data</strong>. The extension will check the review automatically.
                            <div className="d-flex flex-wrap gap-2 mt-1">
            <Image src="/images/virt-auto-mode-start.jpg" alt="Start Automatic Review Checker on Virtalibry" className="img-fluid rounded border" style={{maxHeight:"220px"}} />
          </div>
                        </li>
                    </ol>

                    {/* Mini troubleshooting */}
                    <div className="alert alert-light border py-1 px-2 mb-0">
                        <strong>If nothing happens:</strong> ensure the extension is installed & enabled, you’re
                        logged into Amazon in the same browser, then refresh the assignment page and try again.
                    </div>
                </div>
            </Card.Body>
        </Card>
                        )}

                    </> )}
            </Card>
        </>

    );
};

export default ReviewSubmiterUserSide;
